/**
 * Realtime protocol (WebSocket, JSON text frames).
 * Persistent events carry a workspace-wide monotonically increasing `seq`; a reconnecting client sends
 * `hello{lastSeq}` and the gateway replays everything after it, or replies `resync` when too old.
 * Ephemeral events (presence, typing) have no seq and are never replayed.
 */
import { z } from "zod";
import { AgentState, Channel, ChannelMembership, Id, Member, Message, Presence, SidebarFolder, SidebarItem, Timestamp, Workspace } from "./entities.ts";
import { Database, DbView, PageNode, Row } from "./docs.ts";
import { Huddle, HuddleDrawOp, HuddleSignal } from "./huddles.ts";
import { MailDomain, MailThread, Mailbox } from "./mail.ts";
import { CalendarEvent } from "./calendar.ts";
import { UserGroup, UserGroupPolicy } from "./userGroups.ts";
import { AppHome, EphemeralMessage, ViewStack } from "./apps.ts";

export const PersistentEvent = z.discriminatedUnion("type", [
  z.object({ type: z.literal("message.created"), message: Message }),
  z.object({ type: z.literal("message.updated"), message: Message }),
  z.object({ type: z.literal("message.deleted"), channelId: Id, messageId: Id, threadRootId: Id.optional() }),
  z.object({ type: z.literal("reaction.added"), channelId: Id, messageId: Id, emoji: z.string(), userId: Id }),
  z.object({ type: z.literal("reaction.removed"), channelId: Id, messageId: Id, emoji: z.string(), userId: Id }),
  z.object({ type: z.literal("channel.created"), channel: Channel }),
  z.object({ type: z.literal("channel.updated"), channel: Channel }),
  z.object({ type: z.literal("channel.member_joined"), channelId: Id, userId: Id }),
  z.object({ type: z.literal("channel.member_left"), channelId: Id, userId: Id }),
  /**
   * My read marker moved (sent to my own clients only). Normally forward only; `unread` = "Mark unread" moved it back,
   * so clients apply it even below the marker they have (older clients ignore the field and catch up on bootstrap).
   */
  z.object({ type: z.literal("read.updated"), channelId: Id, userId: Id, lastReadSeq: z.number().int(), unread: z.boolean().optional() }),
  z.object({ type: z.literal("member.updated"), member: Member }),
  /** Name, URL slug, icon, default language or primary owner changed (settings.ts). Audience = everyone. */
  z.object({ type: z.literal("workspace.updated"), workspace: Workspace }),
  // Docs & databases. Audience = members who can see the page (private pages: creator only; guests: AccessPolicy.pageIds).
  z.object({ type: z.literal("page.created"), page: PageNode }),
  /** Title/icon/move/visibility/archive changes (archivedAt set ⇒ moved to trash). */
  z.object({ type: z.literal("page.updated"), page: PageNode }),
  /**
   * The page and its whole subtree are no longer visible to this audience (moved to Private, moved out of a guest's
   * shared subtree, unshared): drop them locally. Sent only to the members who lost access. `myAccess` in page events
   * is the recipient's own (the server sends one event per access level).
   */
  z.object({ type: z.literal("page.removed"), pageId: Id }),
  z.object({ type: z.literal("database.updated"), database: Database }),
  z.object({ type: z.literal("row.created"), row: Row }),
  z.object({ type: z.literal("row.updated"), row: Row }),
  z.object({ type: z.literal("row.deleted"), databaseId: Id, rowId: Id }),
  z.object({ type: z.literal("view.created"), view: DbView }),
  z.object({ type: z.literal("view.updated"), view: DbView }),
  z.object({ type: z.literal("view.deleted"), databaseId: Id, viewId: Id }),
  // Huddles (huddles.ts). Audience = the conversation's members; `huddle` is the full snapshot (drop older versions).
  z.object({ type: z.literal("huddle.started"), huddle: Huddle }),
  z.object({ type: z.literal("huddle.updated"), huddle: Huddle }),
  z.object({ type: z.literal("huddle.ended"), channelId: Id, huddleId: Id }),
  // My sidebar and conversation settings (channels.ts). Audience = only me (my other devices).
  /** Star, mute or notification level of one of my conversations changed. */
  z.object({ type: z.literal("membership.updated"), membership: ChannelMembership }),
  /** Where one of my sidebar items lives changed (no folderId and no rank = back to its default place). */
  z.object({ type: z.literal("sidebar_item.updated"), item: SidebarItem }),
  /** One of my sidebar folders was created or changed. */
  z.object({ type: z.literal("sidebar_folder.updated"), folder: SidebarFolder }),
  /** One of my sidebar folders was deleted: its items go back to their default section or the page tree. */
  z.object({ type: z.literal("sidebar_folder.deleted"), folderId: Id }),
  // Mail (mail.ts). Audience = the mailbox's members (domains: admins).
  /** A thread arrived, changed folder/tab/read state, or got a new message (the full thread; replace by id). */
  z.object({ type: z.literal("mail.thread.updated"), thread: MailThread }),
  z.object({ type: z.literal("mail.mailbox.updated"), mailbox: Mailbox }),
  z.object({ type: z.literal("mail.mailbox.deleted"), mailboxId: Id }),
  z.object({ type: z.literal("mail.domain.updated"), domain: MailDomain }),
  // Calendar (calendar.ts). Audience = organizer + member attendees (everyone for workspace-visible events).
  /** Created or changed (the full event; `myResponse` is the recipient's own, one event per person). */
  z.object({ type: z.literal("calendar.event.updated"), event: CalendarEvent }),
  /** Deleted, or I'm no longer invited: drop it. */
  z.object({ type: z.literal("calendar.event.removed"), eventId: Id }),
  // User groups (userGroups.ts). Audience = everyone.
  /** Created, edited, members changed, disabled or re-enabled (the full group; replace by id). */
  z.object({ type: z.literal("user_group.updated"), group: UserGroup }),
  /** Who can manage user groups changed. */
  z.object({ type: z.literal("user_groups.policy_updated"), policy: UserGroupPolicy }),
  // Blocking (moderation.ts). Audience = only the blocker (my other devices).
  /** The people I blocked changed: the whole list (replace). */
  z.object({ type: z.literal("blocks_changed"), userIds: z.array(Id) }),
  // Files (drive.ts). Audience = the people who can see the items (before and after the change).
  /** Items were created, changed, moved, shared, trashed or deleted: refetch open Files lists / the change feed. */
  z.object({ type: z.literal("drive.changed"), itemIds: z.array(Id) }),
]);
export type PersistentEvent = z.infer<typeof PersistentEvent>;

export const EventEnvelope = z.object({
  seq: z.number().int(),
  ts: Timestamp,
  event: PersistentEvent,
});
export type EventEnvelope = z.infer<typeof EventEnvelope>;

/**
 * A message that should notify its recipient, sent only to that person's sockets right after the message commits.
 * Who gets one is decided with the same rules as mobile push (DMs, @-mentions, @here/@channel, replies in threads I
 * follow; not for the author, a muted conversation unless mentioned by name, or notify = none), plus huddle rings.
 * Desktop and web show it as a local notification when the conversation isn't in front of them
 * (packages/core/src/logic/notify.ts). `userId` = the sender (the caller, for a huddle ring).
 *
 * `about` (absent = "message") says what it is: `thread_reply` (a reply in a thread I follow; `kind` is "dm" in a DM,
 * else "mention") or `huddle` (someone started a huddle in my DM, or rang me into one; `kind` is "dm" in a DM, else
 * "broadcast"; `messageId` = the huddle id, `url` = the conversation) or `reminder` (one of my Later reminders came
 * due; `kind` "dm", `messageId` = the saved message or the Later item id, `url` = Later). `kind` keeps its three values so older clients,
 * which decode it strictly, keep working: an ordinary message in a channel (Notify me about → Channel messages) is `kind` "dm"
 * with the channel's id and url, since clients add an Activity item for the other two.
 */
export const NotificationEvent = z.object({
  type: z.literal("notification"),
  userId: Id,
  /** dm: a DM, a group DM or an ordinary channel message; mention: @-mentioned by name; broadcast: @here / @channel / @everyone. */
  kind: z.enum(["dm", "mention", "broadcast"]),
  channelId: Id,
  messageId: Id,
  /** Set for a thread reply. */
  threadRootId: Id.optional(),
  /** Ready to show, in the recipient's language: "Ana in #design", "Ana" for a DM. */
  title: z.string(),
  /** The message as one line of plain text (mentions and channels by name). */
  body: z.string(),
  /** In-app route of the conversation, or of the thread for a reply (`/acme/c/…`, `/acme/t/…/…`). */
  url: z.string(),
  about: z.enum(["message", "thread_reply", "huddle", "reminder"]).optional(),
  /**
   * With `about: "huddle"`: this person declined my ring into huddle `messageId` (Slack "Ana declined"). Realtime only.
   * `userId` is then my own id, so older clients, which don't know this field, drop it as my own (never as a ring).
   */
  declinedBy: Id.optional(),
  /** Notifications with the same key replace each other (one per conversation, or per thread). */
  collapseKey: z.string(),
  ts: Timestamp,
});
export type NotificationEvent = z.infer<typeof NotificationEvent>;

export const EphemeralEvent = z.discriminatedUnion("type", [
  z.object({ type: z.literal("presence.changed"), userId: Id, presence: Presence }),
  z.object({ type: z.literal("typing"), channelId: Id, userId: Id, threadRootId: Id.optional() }),
  /** Live agent status (drives "Honey: Working…" under the composer and the Agents list). */
  z.object({ type: z.literal("agent.state"), userId: Id, state: AgentState, activity: z.string().optional(), channelId: Id.optional() }),
  NotificationEvent,
  /** A message only I see (apps.ts): a slash command or button answered later through its response_url. */
  z.object({ type: z.literal("app.ephemeral"), message: EphemeralMessage }),
  /** My modal changed (apps.ts views): opened, updated, pushed, popped or closed (`views.views` empty). */
  z.object({ type: z.literal("app.views"), views: ViewStack }),
  /** An app published my Home tab (views.publish; apps.ts AppHome). */
  z.object({ type: z.literal("app.home"), home: AppHome }),
]);
export type EphemeralEvent = z.infer<typeof EphemeralEvent>;

/** Client → server frames. Keepalive uses the literal text "ping" (auto-answered "pong" without waking the DO). */
export const ClientFrame = z.discriminatedUnion("op", [
  z.object({ op: z.literal("hello"), lastSeq: z.number().int().optional(), platform: z.enum(["web", "desktop", "ios", "android"]), clientVersion: z.string() }),
  z.object({ op: z.literal("typing"), channelId: Id, threadRootId: Id.optional() }),
  z.object({ op: z.literal("presence"), presence: Presence }),
  /** Agents only: publish live status (relayed as the `agent.state` ephemeral event; not persisted). */
  z.object({ op: z.literal("agent.state"), state: AgentState, activity: z.string().max(200).optional(), channelId: Id.optional() }),
  /** Draw on the shared screen of the huddle I'm in (relayed as the `huddle.draw` signal to the conversation). */
  z.object({ op: z.literal("huddle.draw"), channelId: Id, huddleId: Id, draw: HuddleDrawOp }),
]);
export type ClientFrame = z.infer<typeof ClientFrame>;

/** Server → client frames. */
export const ServerFrame = z.discriminatedUnion("op", [
  z.object({ op: z.literal("welcome"), connectionId: z.string(), seq: z.number().int(), onlineUserIds: z.array(Id) }),
  /**
   * `prevSeq`: the seq of the previous event this connection was sent (or the replay cursor: hello.lastSeq / the
   * welcome seq). Events are filtered by audience, so a connection's seqs are not contiguous; the client checks
   * continuity against prevSeq instead (prevSeq > my last applied seq → I missed something → resync). Absent only
   * from old gateways (then seq must be lastSeq + 1).
   */
  z.object({ op: z.literal("event"), envelope: EventEnvelope, prevSeq: z.number().int().optional() }),
  z.object({ op: z.literal("ephemeral"), event: EphemeralEvent }),
  /** Live huddle signals (captions, drawing): never stored or replayed; older clients ignore the op. */
  z.object({ op: z.literal("huddle"), signal: HuddleSignal }),
  /** Replay window exceeded — client must call GET /v1/bootstrap and reconnect. */
  z.object({ op: z.literal("resync") }),
  z.object({ op: z.literal("error"), code: z.string(), message: z.string() }),
]);
export type ServerFrame = z.infer<typeof ServerFrame>;
